import { Link } from "wouter";
import { postHref } from "../lib/postNavigation";
import { useI18n } from "../lib/i18n";

type QuickStartMine = {
  jobs: unknown[];
  services: unknown[];
  projects: unknown[];
  companies: unknown[];
  rfqs: unknown[];
};

type RoleAction = {
  label: string;
  href: string;
  buttonClass: string;
};

type RoleDefinition = {
  key: string;
  label: string;
  capabilities: string[];
  stat: (mine: QuickStartMine | null) => { count: number; label: string };
  actions: RoleAction[];
};

const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    key: "employer",
    label: "Employer",
    capabilities: ["hire", "post_job"],
    stat: (mine) => ({ count: mine?.jobs.length ?? 0, label: "jobs posted" }),
    actions: [
      { label: "Post a job", href: postHref("job", "/dashboard"), buttonClass: "btn-red" },
      { label: "Find jobseekers", href: "/marketplace?tab=Jobseekers", buttonClass: "btn-outline" },
    ],
  },
  {
    key: "client",
    label: "Client",
    capabilities: ["post_project"],
    stat: (mine) => ({ count: (mine?.projects.length ?? 0) + (mine?.rfqs.length ?? 0), label: "projects & RFQs" }),
    actions: [
      { label: "Post a project", href: postHref("project", "/dashboard"), buttonClass: "btn-navy" },
      { label: "Browse services", href: "/marketplace?tab=Services", buttonClass: "btn-outline" },
    ],
  },
  {
    key: "worker",
    label: "Worker",
    capabilities: ["find_work", "offer_service"],
    stat: (mine) => ({ count: mine?.services.length ?? 0, label: "services listed" }),
    actions: [
      { label: "Offer a service", href: postHref("service", "/dashboard"), buttonClass: "btn-green" },
      { label: "Browse jobs", href: "/marketplace?tab=Jobs", buttonClass: "btn-outline" },
    ],
  },
  {
    key: "supplier",
    label: "Supplier",
    capabilities: ["promote_company"],
    stat: (mine) => ({ count: mine?.companies.length ?? 0, label: "companies" }),
    actions: [
      { label: "Sell an item", href: postHref("item", "/dashboard"), buttonClass: "btn-gold" },
      { label: "Discover companies", href: "/marketplace?tab=Companies", buttonClass: "btn-outline" },
    ],
  },
];

type QuickStartProps = {
  capabilities: readonly string[];
  mine: QuickStartMine | null;
};

export function QuickStart({ capabilities, mine }: QuickStartProps) {
  const { t } = useI18n();
  const roles = ROLE_DEFINITIONS.filter((role) => role.capabilities.some((capability) => capabilities.includes(capability)));
  const actionKey: Record<string, string> = {
    "Post a job": "dashboard.quickStart.action.postJob",
    "Find jobseekers": "dashboard.quickStart.action.findJobseekers",
    "Post a project": "dashboard.quickStart.action.postProject",
    "Browse services": "dashboard.quickStart.action.browseServices",
    "Offer a service": "dashboard.quickStart.action.offerService",
    "Browse jobs": "dashboard.quickStart.action.browseJobs",
    "Sell an item": "dashboard.quickStart.action.sellItem",
    "Discover companies": "dashboard.quickStart.action.discoverCompanies",
  };
  const statKey: Record<string, string> = {
    employer: "dashboard.quickStart.stat.jobs",
    client: "dashboard.quickStart.stat.projects",
    worker: "dashboard.quickStart.stat.services",
    supplier: "dashboard.quickStart.stat.companies",
  };

  if (roles.length === 0) return null;

  return (
    <section className="card mb-5 hidden overflow-hidden p-4 sm:block sm:p-5" aria-labelledby="quick-start-title">
      <h2 id="quick-start-title" className="mb-4 text-xl font-extrabold tracking-tight text-navy sm:text-base sm:font-bold sm:tracking-normal">{t("dashboard.quickStart.title")}</h2>

      <div className="space-y-3 sm:space-y-2">
        {roles.map((role) => {
          const roleStat = role.stat(mine);
          const [primaryAction, secondaryAction] = role.actions;
          return (
            <article
              key={role.key}
              id={`quick-start-${role.key}`}
              className="scroll-mt-24 grid grid-cols-2 gap-2 rounded-2xl border border-line p-4 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-3 sm:rounded-none sm:border-0 sm:p-0"
            >
              <div className="quick-start-summary col-span-2 min-w-0 sm:col-span-1">
                <h3 className="text-lg font-bold leading-tight text-navy sm:text-sm sm:font-semibold">{t(`dashboard.quickStart.role.${role.key}`)}</h3>
                <p className="mt-1 text-sm text-mut sm:mt-0 sm:text-xs">
                  <strong className="font-semibold text-navy">{roleStat.count}</strong> {t(statKey[role.key] ?? roleStat.label)}
                </p>
              </div>

              <Link href={secondaryAction.href} className={`${secondaryAction.buttonClass} h-11 px-3 text-sm sm:justify-self-end sm:h-9 sm:text-xs`}>
                {t(actionKey[secondaryAction.label] ?? secondaryAction.label)}
              </Link>
              <Link href={primaryAction.href} className={`${primaryAction.buttonClass} h-11 px-3 text-sm sm:min-w-36 sm:justify-self-end sm:h-9 sm:text-xs`}>
                {t(actionKey[primaryAction.label] ?? primaryAction.label)}
              </Link>
            </article>
          );
        })}
      </div>
    </section>
  );
}
